import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Alert, App, Checkbox, Divider, Form, Input, Popover, Select, Spin } from "antd";
import { observer } from "mobx-react";
import { useCallback, useState } from "react";
import { PluginUpdateTermFields } from "./terms.js";
import { __, _i } from "../../../utils/i18n.js";
import { LearnMoreTag } from "../../learnMoreTag.js";
const PLUGIN_UPDATE_FORM_LAYOUT = {
    labelCol: {
        span: 24
    },
    wrapperCol: {
        span: 24
    }
};
const PLUGIN_UPDATE_FORM_LAYOUT_MARGIN_BOTTOM = {
    marginBottom: 8
};
const PluginUpdateForm = observer((param)=>{
    let { onSave, onFailure, footer, pluginUpdate } = param;
    const { message } = App.useApp();
    const [forceFormValidateStatus, setForceFormValidateStatus] = useState(false);
    const { busy, slug, allowsAutoUpdates, needsLicenseKeys, licenses, unlicensedEntries, noUsageEntries, modifiableEntries, invalidKeysError, accountSiteUrl, additionalCheckboxes, licenseKeyHelpUrl, name, potentialNewsletterUser: { firstName, email }, showBlogName, showNetworkWideUpdateIssueNotice } = pluginUpdate;
    const allowNoUsage = licenses.length > 1; // only allow "No usage" within multisite
    const initialValues = {
        licenses: licenses.map((param)=>{
            let { blog, code, installationType, hint, noUsage } = param;
            var _hint_help_match;
            return {
                blog,
                code: code || // Read a code from the hint
                (hint ? (_hint_help_match = hint.help.match(/(\w{8}-\w{4}-\w{4}-\w{4}-\w{12})|(\w{32})/)) == null ? void 0 : _hint_help_match[0] : "") || "",
                installationType: installationType || "",
                noUsage
            };
        }),
        autoUpdates: allowsAutoUpdates,
        terms: false,
        telemetry: false,
        newsletter: false,
        firstName,
        email,
        ...additionalCheckboxes.reduce((p, c)=>{
            p[c.id] = false;
            return p;
        }, {})
    };
    const [form] = Form.useForm();
    const [showTermFields, setShowTermFields] = useState(noUsageEntries.length !== unlicensedEntries.length);
    const handleSave = useCallback(async (values)=>{
        const { licenses: formLicenses, ...meta } = values;
        const newMeta = {
            ...meta,
            licenses: JSON.stringify(needsLicenseKeys ? formLicenses.filter((param)=>{
                let { blog } = param;
                const [foundLicense] = licenses.filter((k)=>k.blog === blog);
                return unlicensedEntries.indexOf(foundLicense) > -1;
            }) : undefined)
        };
        try {
            await pluginUpdate.update(newMeta);
            form.setFieldsValue({
                terms: false,
                telemetry: false,
                newsletter: false
            });
            message.success(__("Your license has been activated!"));
            onSave == null ? void 0 : onSave();
        } catch (e) {
            if (!pluginUpdate.invalidKeysError) {
                message.error(e.responseJSON.message);
            }
            // No error needed cause it is catch trough `invalidKeysError`
            onFailure == null ? void 0 : onFailure();
            throw e;
        }
    }, [
        pluginUpdate,
        onSave,
        licenses,
        unlicensedEntries,
        needsLicenseKeys
    ]);
    const onFinish = useCallback(async (values)=>{
        try {
            await handleSave(values);
        } catch (e) {
        // Silence is golden.
        } finally{
            setForceFormValidateStatus(false);
        }
    }, [
        form,
        handleSave,
        setForceFormValidateStatus
    ]);
    const onFinishFailed = useCallback(()=>{
        setForceFormValidateStatus(true);
        setShowTermFields(true);
    }, []);
    return /*#__PURE__*/ _jsxs(Spin, {
        spinning: busy,
        children: [
            showNetworkWideUpdateIssueNotice && /*#__PURE__*/ _jsxs("div", {
                className: "notice notice-error inline below-h2 notice-alt",
                style: {
                    margin: "0 0 10px 0"
                },
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        children: __("You are using a WordPress mulisite. Due to technical limitations of WordPress core, %s can receive automatic updates in WordPress multisites only if the plugin is enabled network-wide. You can enable the plugin network-wide, but still only license it for specific sites.", name)
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        children: __("Please enable %s network-wide or take care of regular updates manually!", name)
                    })
                ]
            }),
            unlicensedEntries.length > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "description",
                        style: {
                            marginBottom: 15
                        },
                        children: needsLicenseKeys ? __("Activate your %s PRO license to receive regular updates and support.", name) : _i(__("To use all advantages of %s {{strong}}you need a free license{{/strong}}. After license activation you will receive answers to support requests and announcements in your plugin (e.g. also notices for discount actions of the PRO version).", name), {
                            strong: /*#__PURE__*/ _jsx("strong", {})
                        })
                    }),
                    /*#__PURE__*/ _jsxs(Form, {
                        name: `license-form-${slug}`,
                        id: `license-form-${slug}`,
                        form: form,
                        ...PLUGIN_UPDATE_FORM_LAYOUT,
                        initialValues: initialValues,
                        onFinish: onFinish,
                        onFinishFailed: onFinishFailed,
                        onChange: ()=>{
                            setShowTermFields(true);
                        },
                        labelWrap: true,
                        children: [
                            needsLicenseKeys && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(Form.List, {
                                        name: "licenses",
                                        children: (fields)=>fields.map((field)=>{
                                                const { blog } = form.getFieldValue([
                                                    "licenses",
                                                    field.name
                                                ]);
                                                const [foundLicense] = licenses.filter((k)=>k.blog === blog);
                                                // Already licensed, do not show in form again
                                                if (unlicensedEntries.indexOf(foundLicense) === -1) {
                                                    return null;
                                                }
                                                const { busy: licenseBusy, blogName, programmatically, host } = foundLicense;
                                                const lastBlogPutError = invalidKeysError == null ? void 0 : invalidKeysError[blog];
                                                // Hints priority: Form handling, POST-Request, Hint coming from license deactivation
                                                const useHint = forceFormValidateStatus ? {} : lastBlogPutError || foundLicense.hint;
                                                const isLicenseMaxUsagesReached = !!(lastBlogPutError == null ? void 0 : lastBlogPutError.debug.errors["LicenseMaxUsagesReached"]);
                                                return /*#__PURE__*/ _jsxs(Spin, {
                                                    spinning: licenseBusy,
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Form.Item, {
                                                            noStyle: true,
                                                            shouldUpdate: (prevValues, nextValues)=>prevValues.licenses[field.key].noUsage !== nextValues.licenses[field.key].noUsage,
                                                            children: (param)=>{
                                                                let { getFieldValue } = param;
                                                                const noUsage = getFieldValue([
                                                                    "licenses",
                                                                    field.key,
                                                                    "noUsage"
                                                                ]);
                                                                return /*#__PURE__*/ _jsxs(Form.Item, {
                                                                    label: /*#__PURE__*/ _jsxs("span", {
                                                                        children: [
                                                                            unlicensedEntries.length > 1 || showBlogName ? _i(__("Installation type and license key for {{strong}}%s{{/strong}}", blogName), {
                                                                                strong: /*#__PURE__*/ _jsx("strong", {})
                                                                            }) : __("Installation type and license key"),
                                                                            " ",
                                                                            /*#__PURE__*/ _jsx(LearnMoreTag, {
                                                                                url: licenseKeyHelpUrl
                                                                            })
                                                                        ]
                                                                    }),
                                                                    ...useHint,
                                                                    required: true,
                                                                    style: PLUGIN_UPDATE_FORM_LAYOUT_MARGIN_BOTTOM,
                                                                    children: [
                                                                        !noUsage && /*#__PURE__*/ _jsx(_Fragment, {
                                                                            children: /*#__PURE__*/ _jsx(Form.Item, {
                                                                                name: [
                                                                                    field.name,
                                                                                    "code"
                                                                                ],
                                                                                noStyle: true,
                                                                                rules: [
                                                                                    {
                                                                                        // Allow UUID format and additionally a custom 32-length string
                                                                                        pattern: /(^\w{8}-\w{4}-\w{4}-\w{4}-\w{12}$)|(^\w{32}$)/,
                                                                                        required: true,
                                                                                        message: __("Please enter a valid license key!")
                                                                                    }
                                                                                ],
                                                                                normalize: (value)=>value == null ? void 0 : value.trim(),
                                                                                children: /*#__PURE__*/ _jsx(Input, {
                                                                                    placeholder: programmatically ? programmatically.code : "XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX",
                                                                                    disabled: !!programmatically,
                                                                                    addonBefore: /*#__PURE__*/ _jsx(Popover, {
                                                                                        destroyTooltipOnHide: true,
                                                                                        overlayStyle: {
                                                                                            maxWidth: "370px"
                                                                                        },
                                                                                        content: /*#__PURE__*/ _jsxs("div", {
                                                                                            className: "wp-clearfix",
                                                                                            children: [
                                                                                                /*#__PURE__*/ _jsx("p", {
                                                                                                    style: {
                                                                                                        marginTop: 0
                                                                                                    },
                                                                                                    children: _i(__("{{strong}}Production:{{/strong}} Use this, when your site is {{i}}live{{/i}} and {{i}}publicly available{{/i}} to your website visitors."), {
                                                                                                        strong: /*#__PURE__*/ _jsx("strong", {}),
                                                                                                        i: /*#__PURE__*/ _jsx("i", {})
                                                                                                    })
                                                                                                }),
                                                                                                /*#__PURE__*/ _jsx("p", {
                                                                                                    children: _i(__("{{strong}}Development:{{/strong}} Use this, when your site is {{i}}not yet live{{/i}}, or it is a {{i}}staging environment{{/i}} of your site."), {
                                                                                                        strong: /*#__PURE__*/ _jsx("strong", {}),
                                                                                                        i: /*#__PURE__*/ _jsx("i", {})
                                                                                                    })
                                                                                                }),
                                                                                                /*#__PURE__*/ _jsx("p", {
                                                                                                    style: {
                                                                                                        marginBottom: 0
                                                                                                    },
                                                                                                    children: __("You can change the installation-type at any time by deactivating the license and activate it again.")
                                                                                                })
                                                                                            ]
                                                                                        }),
                                                                                        title: /*#__PURE__*/ _jsxs(_Fragment, {
                                                                                            children: [
                                                                                                __("What is an installation type?"),
                                                                                                "\xa0",
                                                                                                /*#__PURE__*/ _jsx(LearnMoreTag, {
                                                                                                    url: __("https://devowl.io/knowledge-base/license-installation-type/")
                                                                                                })
                                                                                            ]
                                                                                        }),
                                                                                        placement: "topLeft",
                                                                                        trigger: "hover",
                                                                                        children: /*#__PURE__*/ _jsx("div", {
                                                                                            children: /*#__PURE__*/ _jsx(Form.Item, {
                                                                                                name: [
                                                                                                    field.name,
                                                                                                    "installationType"
                                                                                                ],
                                                                                                noStyle: true,
                                                                                                rules: [
                                                                                                    {
                                                                                                        required: true,
                                                                                                        message: __("Please choose an installation type!")
                                                                                                    }
                                                                                                ],
                                                                                                children: /*#__PURE__*/ _jsxs(Select, {
                                                                                                    placeholder: __("Installation type"),
                                                                                                    disabled: !!programmatically,
                                                                                                    children: [
                                                                                                        /*#__PURE__*/ _jsx(Select.Option, {
                                                                                                            value: "",
                                                                                                            disabled: true,
                                                                                                            children: programmatically ? programmatically.type === "development" ? __("Development") : __("Production") : __("Select installation type...")
                                                                                                        }),
                                                                                                        /*#__PURE__*/ _jsx(Select.Option, {
                                                                                                            value: "production",
                                                                                                            children: __("Production")
                                                                                                        }),
                                                                                                        /*#__PURE__*/ _jsx(Select.Option, {
                                                                                                            value: "development",
                                                                                                            children: __("Development")
                                                                                                        })
                                                                                                    ]
                                                                                                })
                                                                                            })
                                                                                        })
                                                                                    })
                                                                                })
                                                                            })
                                                                        }),
                                                                        allowNoUsage && /*#__PURE__*/ _jsx(Form.Item, {
                                                                            name: [
                                                                                field.name,
                                                                                "noUsage"
                                                                            ],
                                                                            valuePropName: "checked",
                                                                            style: {
                                                                                marginTop: noUsage ? -25 : 0,
                                                                                marginBottom: 0
                                                                            },
                                                                            children: /*#__PURE__*/ _jsx(Checkbox, {
                                                                                children: __("I do not want to license and use the plugin for this site within my multisite.")
                                                                            })
                                                                        })
                                                                    ]
                                                                });
                                                            }
                                                        }),
                                                        programmatically && /*#__PURE__*/ _jsx("div", {
                                                            className: "notice notice-warning inline below-h2 notice-alt",
                                                            style: {
                                                                margin: "0 0 10px 0"
                                                            },
                                                            children: /*#__PURE__*/ _jsxs("p", {
                                                                children: [
                                                                    _i(__("This license cannot be activated manually because it is configured programmatically. That means you have used the {{a}}activation filter{{/a}} for host {{code}}%s{{/code}} (Blog ID: %d). Unfortunately, something went wrong during the license activation.", host, blog), {
                                                                        code: /*#__PURE__*/ _jsx("code", {}),
                                                                        a: /*#__PURE__*/ _jsx("a", {
                                                                            href: "https://docs.devowl.io/real-cookie-banner/hooks/DevOwl_RealProductManager_License_Programmatic_$slug.html",
                                                                            target: "_blank",
                                                                            rel: "noreferrer"
                                                                        })
                                                                    }),
                                                                    " ",
                                                                    "•",
                                                                    " ",
                                                                    /*#__PURE__*/ _jsx("a", {
                                                                        className: "button-link",
                                                                        onClick: async ()=>{
                                                                            try {
                                                                                await foundLicense.retry();
                                                                                if (foundLicense.hint) {
                                                                                    message.error(foundLicense.hint.help);
                                                                                }
                                                                            } catch (e) {
                                                                                message.error(e.responseJSON.message);
                                                                            }
                                                                        },
                                                                        children: __("Retry activation")
                                                                    })
                                                                ]
                                                            })
                                                        }),
                                                        isLicenseMaxUsagesReached && /*#__PURE__*/ _jsx(Alert, {
                                                            type: "info",
                                                            showIcon: true,
                                                            message: __("I have purchased a license that can be used on multiple websites! What can I do?"),
                                                            description: /*#__PURE__*/ _jsxs(_Fragment, {
                                                                children: [
                                                                    /*#__PURE__*/ _jsx("p", {
                                                                        children: _i(__("After purchasing one of our products, only one license key is generated by default, even if your purchase covers multiple websites. This license key can be used for only one website by default. To add more websites to your purchase, you can generate additional license keys within your quota in the {{a}}Customer Center{{/a}}."), {
                                                                            a: /*#__PURE__*/ _jsx("a", {
                                                                                href: accountSiteUrl,
                                                                                target: "_blank",
                                                                                rel: "noreferrer"
                                                                            })
                                                                        })
                                                                    }),
                                                                    /*#__PURE__*/ _jsx("a", {
                                                                        className: "button button-primary",
                                                                        href: accountSiteUrl,
                                                                        target: "_blank",
                                                                        rel: "noreferrer",
                                                                        children: __("Add licenses in the customer center now")
                                                                    }),
                                                                    "\xa0",
                                                                    /*#__PURE__*/ _jsx("a", {
                                                                        className: "button",
                                                                        target: "_blank",
                                                                        href: __("https://devowl.io/knowledge-base/the-limit-of-activated-clients-for-this-license-has-already-been-reached/"),
                                                                        rel: "noreferrer",
                                                                        children: __("Learn more")
                                                                    })
                                                                ]
                                                            })
                                                        })
                                                    ]
                                                }, field.key);
                                            })
                                    }),
                                    /*#__PURE__*/ _jsx(Divider, {
                                        type: "horizontal",
                                        style: {
                                            margin: "10px 0"
                                        }
                                    })
                                ]
                            }),
                            modifiableEntries.length > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        style: {
                                            display: showTermFields ? "block" : "none"
                                        },
                                        children: /*#__PURE__*/ _jsx(PluginUpdateTermFields, {
                                            pluginUpdate: pluginUpdate
                                        })
                                    }),
                                    footer
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
});
export { PluginUpdateForm, PLUGIN_UPDATE_FORM_LAYOUT, PLUGIN_UPDATE_FORM_LAYOUT_MARGIN_BOTTOM };
